import * as Headless from '@headlessui/react'
import type React from 'react'
import { forwardRef } from 'react'
import { cn } from '../../shared/cn'
import { Caption } from '../typography/Caption'
import { P2 } from '../typography/Paragraph/P2'
import { P3 } from '../typography/Paragraph/P3'
import { Avatar } from './Avatar'
import { TouchTarget } from './Button'
import { Link } from './Link'

type UserProps = {
  src?: string | null
  accentColor?: string | null
  fullname: string
  additionalInfo?: string | React.ReactNode
  small?: boolean
  tiny?: boolean
  square?: boolean
  href?: string
  /** Truncate fullname with ellipsis when horizontal space is constrained. Default: natural width. */
  truncateFullname?: boolean
}

export function User({ ...props }: UserProps & React.ComponentPropsWithoutRef<'div'>) {
  const {
    src,
    accentColor,
    fullname,
    additionalInfo,
    small,
    tiny,
    square,
    href,
    truncateFullname
  } = props
  const initials =
    fullname && fullname.length > 0
      ? (fullname[0] + (fullname.split(' ')[1]?.[0] ?? '')).toUpperCase()
      : ''

  const fullnameClassName = cn('font-medium', truncateFullname ? 'truncate' : 'text-nowrap')

  const rootClassName = cn(
    'flex flex-row items-center gap-3',
    truncateFullname && 'min-w-0 w-full max-w-full'
  )

  const content = (
    <>
      <Avatar
        className={cn('shrink-0 text-white', small ? 'size-8' : tiny ? 'size-6' : 'size-10')}
        src={src}
        accentColor={accentColor}
        initials={initials}
        square={square}
      />
      <div className={cn('flex min-w-0 flex-col', truncateFullname && 'flex-1 overflow-hidden')}>
        {small || tiny ? (
          <div className={cn(truncateFullname && 'min-w-0')}>
            <P3 className={fullnameClassName} title={truncateFullname ? fullname : undefined}>
              {fullname}
            </P3>{' '}
            {additionalInfo && (
              <Caption className='text-contentSecondary'>{additionalInfo}</Caption>
            )}
          </div>
        ) : (
          <div className={cn(truncateFullname && 'min-w-0')}>
            <P2 className={fullnameClassName} title={truncateFullname ? fullname : undefined}>
              {fullname}
            </P2>{' '}
            {additionalInfo && (
              <P3 className='font-normal text-contentSecondary'>{additionalInfo}</P3>
            )}
          </div>
        )}
      </div>
    </>
  )

  if (href) {
    return (
      <Link href={href} target='_blank' className={rootClassName}>
        {content}
      </Link>
    )
  }

  return <div className={rootClassName}>{content}</div>
}

type UserButtonLinkProps = UserProps &
  Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'> & {
    href: string
  }

type UserButtonButtonProps = UserProps & Omit<Headless.ButtonProps, 'as'>

export const UserButton = forwardRef(function UserButton(
  { ...props }: UserButtonLinkProps | UserButtonButtonProps,
  ref: React.ForwardedRef<HTMLElement>
) {
  const { src, accentColor, fullname, additionalInfo } = props

  if ('href' in props && typeof props.href === 'string') {
    const linkProps = props as UserButtonLinkProps
    return (
      <Link {...linkProps} ref={ref as React.ForwardedRef<HTMLAnchorElement>}>
        <TouchTarget>
          <User
            fullname={fullname}
            additionalInfo={additionalInfo}
            src={src}
            accentColor={accentColor}
          ></User>
        </TouchTarget>
      </Link>
    )
  }

  const buttonProps = props as UserButtonButtonProps
  const { className, ...restButtonProps } = buttonProps
  return (
    <Headless.Button {...restButtonProps} className={cn('cursor-pointer', className)} ref={ref}>
      <TouchTarget>
        <User
          fullname={fullname}
          additionalInfo={additionalInfo}
          src={src}
          accentColor={accentColor}
        ></User>
      </TouchTarget>
    </Headless.Button>
  )
})
